<template>
  <section
    ref="section"
    class="circles-timeline-module-component">
    <div class="vfx-container">
      <div class="vfx inner-container">
        <div class="line-w-circle">
          <div
            ref="line"
            class="line"></div>
          <div class="sphere-rails">
            <div
              ref="sphere"
              class="sphere" />
          </div>
        </div>
      </div>
    </div>
    <div class="circles-timeline-module-component-wrapper">
      <div class="content inner-container">
        <div class="text-content">
          <div class="text-content-left">
            <h2
              class="text-content-left-title"
              v-html="data.left.title" />
            <div
              class="text-content-left-sub"
              v-html="data.left.sub" />
          </div>
          <div
            class="text-content-right"
            v-html="data.text" />
        </div>
        <div
          v-if="device === 'desktop'"
          class="desktop-list">
          <div class="circles-top-line" />
          <ul class="circles">
            <li
              v-for="(circle, idx) in data.circles"
              :key="`dt-circle-${idx}`"
              class="circle-container">
              <div
                class="circle"
                :class="genClasses('circle', idx, data.circles.length)">
                <span
                  class="circle-text"
                  v-html="circle.text" />
              </div>
              <div
                class="line-circle"
                :class="genClasses('line-circle', idx, data.circles.length)" />
              <img
                v-if="idx + 1 !== data.circles.length"
                src="@/assets/img/arrow-right.svg"
                class="arrow"
                alt="arrow-nav" />
            </li>
          </ul>
        </div>
        <div
          v-else
          class="mobile-carousel">
          <div class="indexes">
            <div
              v-for="(_circle, idx) in data.circles"
              :key="`mb-circle-idx-${idx}`"
              class="line-circle"
              :class="[
                genClasses('line-circle', idx, data.circles.length),
                { active: idx === current },
              ]" />
            <div class="line" />
          </div>
          <div class="carousel">
            <button
              class="carousel-arrow left"
              @click="prev()">
              <img src="@/assets/img/arrow-right.svg" />
            </button>
            <div
              ref="circleMobile"
              class="circle"
              :class="genClasses('circle', current, data.circles.length)">
              <span
                class="circle-text"
                v-html="data.circles[current].text" />
            </div>
            <button
              class="carousel-arrow right"
              :class="{ 'pointer-events-none': isAnimating }"
              @click="next()">
              <img src="@/assets/img/arrow-right.svg" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
 {
  "NAME": "CirclesTimelineComponent",
  "CLASSNAME": "circles-timeline-module-component",
  "URL": "http://lifemine.local/templates/"
 }
#ENDMETA */
import useUtils from '@/composables/useUtils'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

import { onBeforeUnmount, onMounted, ref } from 'vue'
const { device, genUUID } = useUtils()

const props = defineProps(['data'])
const animID = ref(genUUID())
const sphere = ref()
const line = ref()
const section = ref()
const current = ref(0)
const circleMobile = ref()
const isAnimating = ref(false)

const sphereAnimIn = () => {
  gsap.to(sphere.value, {
    y: -200,
    ease: 'power1.out',
    scrollTrigger: {
      trigger: section.value,
      scrub: true,
      start: 'top center+=20%',
      end: 'top+=40% center',
      //markers: true,
      id: animID.value,
    },
  })
}

const lineAnim = () => {
  gsap.fromTo(
    line.value,
    {
      height: '0%',
    },
    {
      height: `100%`,
      // ease: 'power1.out',
      scrollTrigger: {
        trigger: section.value,
        scrub: true,
        start: 'top+=100px center+=20%',
        end: `bottom+=100px center+=20%`,
        // markers: true,
        id: animID.value,
      },
    }
  )
}

const genClasses = (classname, idx, total) => {
  const classes = []
  if (idx === 0) {
    classes.push(`first-${classname}`)
  }
  if (idx === 1) {
    classes.push(`second-${classname}`)
  }
  if (idx === 2) {
    classes.push(`third-${classname}`)
  }
  if (idx + 1 === total) {
    classes.push(`last-${classname}`)
  }
  return classes
}

const fadeIn = async () => {
  isAnimating.value = true
  await gsap.to(circleMobile.value, { opacity: 0 })
}

const fadeOut = async () => {
  await gsap.to(circleMobile.value, { opacity: 1 })
  isAnimating.value = false
}

const next = async () => {
  if (current.value < props.data.circles.length - 1) {
    await fadeIn()
    current.value++
    await fadeOut()
  }
}

const prev = async () => {
  if (current.value > 0) {
    await fadeIn()
    current.value--
    await fadeOut()
  }
}

onMounted(() => {
  sphereAnimIn()
  lineAnim()
})

onBeforeUnmount(() => {
  ScrollTrigger.getById(animID.value)?.kill()
})
</script>

<style lang="scss">
.circles-timeline-module-component {
  @apply relative;

  .vfx-container {
    @apply -top-10 absolute w-full h-[calc(100%_+_79px)];

    .vfx {
      @apply h-full w-full;

      .line-w-circle {
        @apply relative h-full w-full;

        .line {
          background: linear-gradient(180deg, #99b4ff 59%, #ffad39 100%);
        }

        .line,
        .sphere-rails {
          @apply absolute left-0 top-0 w-[1px] h-full flex items-center justify-center overflow-visible;

          .sphere {
            @apply translate-y-[-300px] min-w-[30px] min-h-[30px] rounded-full bg-lm_lavender blur-[2px];
            background-color: #99b4ff;
            box-shadow: 0 0 20px #99b4ff;
          }
        }
      }
    }
  }

  &-wrapper {
    @apply my-[39px] bg-[url('@/assets/img/circlesSectionBg.jpg')] bg-cover bg-no-repeat py-[68px];

    .content {
      .text-content {
        @apply pl-[30px] flex flex-col gap-2 mb-[69px] lg:mb-[110px] lg:gap-[76px] lg:px-[33px] xl:flex-row;

        &-left {
          @apply flex flex-col gap-[37px] lg:w-full lg:gap-[43px] xl:max-w-[500px];

          &-title {
            @apply heading-3 pr-[50px] lg:heading-4 lg:p-0;
            sup {
              @apply text-inherit text-xs -top-4 font-semibold;
            }
          }

          &-sub {
            p {
              @apply small lg:large;
            }
          }
        }

        &-right {
          @apply flex flex-col gap-2 lg:flex-1 lg:gap-4;

          p {
            @apply small lg:medium;
            sup {
              @apply text-inherit text-xs -top-2.5;
            }
          }
        }
      }

      .desktop-list {
        @apply relative;

        .circles-top-line {
          @apply absolute top-0 w-full h-px;
          background: linear-gradient(90deg, #99b4ff 34.55%, #ffad39 98.7%);
        }

        .circles {
          @apply flex;

          .circle-container {
            @apply relative flex-1 flex justify-center pt-[68px];

            .circle {
              @apply h-[175px] w-[175px] xl:h-[231px] xl:w-[231px] flex items-center justify-center bg-cover;

              &-text {
                @apply text-center medium w-[150px] xl:large xl:w-[175px];
                sup {
                  @apply text-inherit text-xs -top-2.5;
                }
              }
            }

            .line-circle {
              @apply absolute -top-[8px] w-[17px] h-[17px] rounded-full;
            }

            .arrow {
              @apply -right-[calc(23px/2)] absolute;
              top: calc(50% + 34px);
              transform: translateY(-50%);
            }
          }
        }
      }

      .mobile-carousel {
        @apply flex flex-col items-center gap-[53px];

        .indexes {
          @apply relative flex justify-between w-full;

          .line-circle {
            @apply bg-lm_lavender w-3 h-3 rounded-full transition-all;

            &:first-child {
              @apply ml-[72px];
            }

            &.active {
              @apply scale-[1.666];
            }
          }

          .line {
            @apply -z-1 absolute top-[5.5px] h-px w-full bg-red-500;
          }
        }

        .carousel {
          @apply w-full max-w-[500px] flex justify-between items-center;

          &-arrow {
            &.left {
              @apply rotate-180;
            }
          }

          .circle {
            @apply flex items-center justify-center w-[231px] h-[231px];

            &-text {
              @apply w-[177px] text-center large;
            }
          }
        }
      }
    }
  }

  // global / repeated styles
  .first-line-circle {
    @apply bg-lm_lavender;
  }

  .second-line-circle {
    background: linear-gradient(140deg, #99b4ff 28.68%, #ffad39 99.16%);
  }

  .third-line-circle {
    background: linear-gradient(135deg, #99b4ff -5.83%, #ffad39 99.16%);
  }

  .last-line-circle {
    background: none;
    @apply bg-lm_yellow;
  }

  .first-circle {
    @apply bg-[url('@/assets/img/ellipse-0.svg')];
  }

  .second-circle {
    @apply bg-[url('@/assets/img/ellipse-25.svg')];
  }

  .third-circle {
    @apply bg-[url('@/assets/img/ellipse-75.svg')];
  }

  .last-circle {
    @apply bg-[url('@/assets/img/ellipse-100.svg')];
  }
}
</style>
